import moment from 'moment';
import { Button } from 'reactstrap';
import isAuthorized from '../../../../../helpers/isAuthorized';
import {
  canUpdatePerformanceModule,
  canDeletePerformanceManagement,
} from '../../../../../helpers/performanceManagementPermissions';
import { Context as AuthContext } from '../../../../../contexts/AuthContext';
import React, { useContext } from 'react';

export const getImprovementPendingColumns = (
  setItemPerformanceImprovementSummaryModal: (data: any) => void,
  setShowPerformanceImprovementSummaryModal: (show: boolean) => void,
  handleApproveOrDeny: (e: React.FormEvent, action: string, type: string, id: string) => void,
  handleEdit: (row: any) => void,
  options?: {
    includeTypeColumn?: boolean;
    onFinalize?: (row: any) => void;
    onDelete?: (e: React.FormEvent, row: any) => void;
  },
) => {
  const { user }: any = useContext(AuthContext);

  const baseColumns = [
    {
      name: 'Employee',
      selector: (row: any) => `${row?.name}`,
      sortable: true,
      width: '300px',
    },
    {
      name: 'Created',
      selector: (row: any) => row.createdAt,
      sortable: true,
      format: (row: any) => (row.createdAt ? moment.utc(row.createdAt).format('MM/DD/YYYY') : '-'),
      width: '120px',
    },
    {
      name: 'Date',
      selector: (row: any) => row.date,
      sortable: true,
      format: (row: any) => (row.date ? moment.utc(row.date).format('MM/DD/YYYY') : '-'),
      width: '120px',
    },
    {
      name: 'Policy Violated',
      selector: (row: any) => {
        if (row.policyViolations && Array.isArray(row.policyViolations)) {
          return row.policyViolations.map((pv: any) => pv.name || pv).join(', ');
        }
        if (row.policyViolationIds && Array.isArray(row.policyViolationIds)) {
          return row.policyViolationIds.join(', ');
        }
        if (row.policyViolation?.name) {
          return row.policyViolation.name;
        }
        if (row.policy_violated) {
          return row.policy_violated;
        }
        return 'N/A';
      },
      sortable: true,
      width: '230px',
    },
    {
      name: 'Actions',
      cell: (row: any) => (
        <div className="d-flex justify-content-center">
          {row?.pendingRowKind === 'pending_finalization' ? (
            <>
              {canUpdatePerformanceModule('PerformanceImprovement', user) && (
                <Button
                  outline
                  color="success"
                  size="sm"
                  style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                  title="Finalize"
                  onClick={() => options?.onFinalize?.(row)}
                >
                  <i className="fas fa-file-signature" />
                </Button>
              )}
              {canDeletePerformanceManagement(user) && (
                <Button
                  outline
                  color="danger"
                  size="sm"
                  style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                  title="Delete"
                  onClick={(e: any) => options?.onDelete?.(e, row)}
                >
                  <i className="fas fa-trash" />
                </Button>
              )}
            </>
          ) : (
            <>
              <Button
                outline
                color="info"
                size="sm"
                style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                title="View Details"
                onClick={() => {
                  const dataRow = row;
                  dataRow.data = row;
                  setItemPerformanceImprovementSummaryModal([dataRow]);
                  setShowPerformanceImprovementSummaryModal(true);
                }}
              >
                <i className="fas fa-eye" />
              </Button>
              {canUpdatePerformanceModule('PerformanceImprovement', user) && (
                <Button
                  outline
                  color="warning"
                  size="sm"
                  style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                  title="Edit"
                  onClick={() => handleEdit(row)}
                >
                  <i className="fas fa-edit" />
                </Button>
              )}
              {isAuthorized({ moduleName: 'PerformanceImprovement', functionName: 'Reject' }, user) && (
                <Button
                  outline
                  color="danger"
                  size="sm"
                  style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                  title="Deny"
                  onClick={(e: any) => {
                    handleApproveOrDeny(e, 'reject', 'performance-improvement', row?.id);
                  }}
                >
                  <i className="fas fa-ban" />
                </Button>
              )}
              {canDeletePerformanceManagement(user) && (
                <Button
                  outline
                  color="danger"
                  size="sm"
                  style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                  title="Delete"
                  onClick={(e: any) => options?.onDelete?.(e, row)}
                >
                  <i className="fas fa-trash" />
                </Button>
              )}
            </>
          )}
        </div>
      ),
      width: '120px',
      center: true,
    },
  ];

  if (!options?.includeTypeColumn) {
    return baseColumns;
  }

  return [
    baseColumns[0],
    {
      name: 'Type',
      selector: (row: any) => (row?.pendingRowKind === 'pending_finalization' ? 'Pending Finalization' : 'Draft'),
      sortable: true,
      width: '190px',
      cell: (row: any) => (
        <span style={{ fontWeight: 700 }}>
          {row?.pendingRowKind === 'pending_finalization' ? 'Pending Finalization' : 'Draft'}
        </span>
      ),
    },
    ...baseColumns.slice(1),
  ];
};
